import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { DraggableCore } from 'react-draggable';

import style from './style.module.css';

const DEFAULT_HEIGHT = 330;
const MIN_HEIGHT = 150;
const MIN_REST_HEIGHT = 300;

const readStoredHeight = (storageKey, defaultHeight) => {
  try {
    const storedHeight = Number(window.localStorage.getItem(storageKey));

    return storedHeight >= MIN_HEIGHT ? storedHeight : defaultHeight;
  } catch (error) {
    return defaultHeight;
  }
};

const storeHeight = (storageKey, height) => {
  try {
    window.localStorage.setItem(storageKey, String(height));
  } catch (error) {
    // Storage may be unavailable (private mode); the height just won't persist.
  }
};

const clampHeight = height => {
  const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - MIN_REST_HEIGHT);

  return Math.min(Math.max(height, MIN_HEIGHT), maxHeight);
};

const ResizableSectionComponent = ({ storageKey, defaultHeight = DEFAULT_HEIGHT, className, children }) => {
  const handleNodeRef = useRef(null);
  const pointerOffsetRef = useRef(0);
  const resizeFrameRef = useRef(null);
  const [height, setHeight] = useState(() => readStoredHeight(storageKey, defaultHeight));

  useEffect(() => () => cancelAnimationFrame(resizeFrameRef.current), []);

  const handleDragStart = useCallback(
    (event, { y }) => {
      pointerOffsetRef.current = height - y;
    },
    [height],
  );

  const handleDrag = useCallback((event, { y }) => {
    setHeight(clampHeight(y + pointerOffsetRef.current));

    // Panels below (e.g. CompanyTarget) and grids recalculate their height on window resize only.
    cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  const handleDragStop = useCallback(
    (event, { y }) => {
      storeHeight(storageKey, clampHeight(y + pointerOffsetRef.current));
    },
    [storageKey],
  );

  return (
    <div className={classNames(style.host, className)} style={{ height }}>
      {children}
      <DraggableCore nodeRef={handleNodeRef} onDrag={handleDrag} onStart={handleDragStart} onStop={handleDragStop}>
        <span ref={handleNodeRef} className={style.handle} />
      </DraggableCore>
    </div>
  );
};

ResizableSectionComponent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  defaultHeight: PropTypes.number,
  storageKey: PropTypes.string.isRequired,
};

/** Full-width section whose height is changed by dragging the bottom handle and kept in localStorage. */
export const ResizableSection = memo(ResizableSectionComponent);
